import { createRef, useCallback, useEffect, useRef, useState } from "react";
import {
  AccessibilityInfo,
  Animated,
  Easing,
  PanResponder,
  ScrollView,
  useWindowDimensions
} from "react-native-web";
import { isClient, isWeb, useMedia, View } from "@tamagui/core";
import { Dialog } from "@tamagui/dialog";
import { Box } from "../../../../components/Box";
import { PressableBox } from "../../../../components/Box/PressableBox";
import {
  ChevronBackButton,
  ChevronForwardButton,
  CloseButton
} from "../../../../components/Button/IconButton";
import { Image } from "../../../../components/Image";
import { Text } from "../../../../components/Text";
import {
  CABINET_QUERY_PARAMS_FOR_LIGHTBOX_PRIMARY,
  CABINET_QUERY_PARAMS_LIGHTBOX_CAROUSEL
} from "./helpers";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
const Lightbox = ({
  analyticsName,
  photoUrls,
  isOpen,
  handleClose,
  mobileTitle,
  footer,
  selectedPhotoIndex
}) => {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions(), photoRefs = useRef([]), [primaryPhotoIndex, setPrimaryPhotoIndex] = useState(
    selectedPhotoIndex || 0
  ), containerRef = useRef(null), [panResponder, setPanResponder] = useState(null), [isLandscape, setIsLandscape] = useState(!1), offsetX = useRef(new Animated.Value(0)).current, currentOffset = useRef(0), carouselRef = useRef(null), lastNavigationSource = useRef(null), primaryPhotoRefs = useRef(
    photoUrls.map((_) => createRef())
  ), handleOnPress = (index) => {
    setPrimaryPhotoIndex(index);
  };
  useEffect(() => {
    isClient && Math.abs(window.orientation) === 90 && setIsLandscape(!0);
  }, []), useEffect(() => {
    selectedPhotoIndex != null && setPrimaryPhotoIndex(selectedPhotoIndex);
  }, [selectedPhotoIndex]), useEffect(() => {
    const photoItem = photoRefs.current[primaryPhotoIndex];
    photoItem?.scrollIntoView({ behavior: "smooth", block: "nearest" }), lastNavigationSource.current?.source === "keyboard" && !lastNavigationSource.current.keyboardActivated && photoItem?.focus();
  }, [primaryPhotoIndex]);
  const handleRight = useCallback(
    (source, keyboardActivated = !1) => {
      let newIndex = primaryPhotoIndex + 1;
      return newIndex >= photoUrls.length && (newIndex = 0), setPrimaryPhotoIndex(newIndex), { source, keyboardActivated };
    },
    [primaryPhotoIndex, photoUrls.length]
  ), handleLeft = useCallback(
    (source, keyboardActivated = !1) => {
      let newIndex = primaryPhotoIndex - 1;
      return newIndex < 0 && (newIndex = photoUrls.length - 1), setPrimaryPhotoIndex(newIndex), { source, keyboardActivated };
    },
    [primaryPhotoIndex, photoUrls.length]
  );
  useEffect(() => {
    const responder = PanResponder.create({
      onMoveShouldSetPanResponder: () => !0,
      onPanResponderGrant: () => {
        offsetX.setValue(currentOffset.current);
      },
      onPanResponderMove: (evt, gestureState) => {
        const touches = evt.nativeEvent.touches;
        touches && touches.length < 2 && offsetX.setValue(currentOffset.current + gestureState.dx);
      },
      onPanResponderRelease: (evt, gestureState) => {
        const currentImageIndex = primaryPhotoIndex, threshold = windowWidth / 7;
        let newIndex = currentImageIndex;
        gestureState.dx > threshold ? newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : currentImageIndex : gestureState.dx < -threshold && (newIndex = currentImageIndex < photoUrls.length - 1 ? currentImageIndex + 1 : currentImageIndex), newIndex !== currentImageIndex ? Animated.timing(offsetX, {
          toValue: -newIndex * windowWidth,
          duration: 300,
          easing: Easing.linear,
          useNativeDriver: !isWeb
        }).start(() => {
          currentOffset.current = -newIndex * windowWidth, setPrimaryPhotoIndex(newIndex), AccessibilityInfo.announceForAccessibility(`Image ${newIndex + 1}`);
        }) : Animated.spring(offsetX, {
          toValue: currentOffset.current,
          useNativeDriver: !isWeb
        }).start();
      }
    });
    setPanResponder(responder);
  }, [windowWidth, offsetX, primaryPhotoIndex, photoUrls.length]), useEffect(() => {
    offsetX.setValue(-primaryPhotoIndex * windowWidth), currentOffset.current = -primaryPhotoIndex * windowWidth;
  }, [windowWidth, offsetX, primaryPhotoIndex]);
  const handleKeyDownEvents = useCallback(
    (e) => {
      e instanceof KeyboardEvent && (e.key === "ArrowRight" ? lastNavigationSource.current = handleRight("keyboard") : e.key === "ArrowLeft" ? lastNavigationSource.current = handleLeft("keyboard") : e.key === "Escape" && handleClose());
    },
    [handleClose, handleLeft, handleRight]
  ), handleChevronPress = useCallback(
    (direction) => (event) => {
      let keyboardActivated = !1;
      "nativeEvent" in event && event.nativeEvent instanceof KeyboardEvent ? keyboardActivated = !0 : event.type === "click" && (keyboardActivated = !1), lastNavigationSource.current = direction === "left" ? handleLeft("button", keyboardActivated) : handleRight("button", keyboardActivated);
    },
    [handleLeft, handleRight]
  );
  useEffect(() => (isWeb && document.addEventListener("keydown", handleKeyDownEvents), () => {
    isWeb && document.removeEventListener("keydown", handleKeyDownEvents);
  }), [handleKeyDownEvents]), useEffect(() => {
    windowWidth > windowHeight ? setIsLandscape(!0) : setIsLandscape(!1);
  }, [windowHeight, windowWidth]);
  const renderImages = () => photoUrls.map((photo, index) => index >= primaryPhotoIndex - 2 && index <= primaryPhotoIndex + 2 ? /* @__PURE__ */ jsx(
    Box,
    {
      width: windowWidth,
      height: "100%",
      "aria-hidden": index !== primaryPhotoIndex,
      flexGrow: 1,
      children: /* @__PURE__ */ jsx(
        Image,
        {
          alt: "Current primary photograph in lightbox",
          source: {
            uri: `${photoUrls[index]}${CABINET_QUERY_PARAMS_FOR_LIGHTBOX_PRIMARY}`
          },
          resizeMode: "contain",
          height: "100%",
          width: "100%",
          mb: "$10x",
          ref: primaryPhotoRefs.current[index],
          enableZoom: !0
        }
      )
    },
    `main-photos-${index}`
  ) : /* @__PURE__ */ jsx(
    Box,
    {
      width: windowWidth,
      height: "100%",
      "aria-hidden": !0
    },
    `main-photo-empty-${index}`
  )), { largerThanSM, smallerThanMD } = useMedia();
  return /* @__PURE__ */ jsx(Dialog, { modal: !0, open: isOpen, children: /* @__PURE__ */ jsxs(Dialog.Portal, { children: [
    /* @__PURE__ */ jsx(
      Dialog.Overlay,
      {
        animation: "accelerateDecelerateFastest",
        opacity: 0.5,
        enterStyle: { opacity: 0 },
        exitStyle: { opacity: 0 }
      },
      "overlay"
    ),
    /* @__PURE__ */ jsxs(
      Dialog.Content,
      {
        bordered: !0,
        elevate: !0,
        animateOnly: ["transform", "opacity"],
        animation: [
          "accelerateDecelerateFastest",
          {
            opacity: {
              overshootClamping: !0
            }
          }
        ],
        enterStyle: { x: 0, y: -20, opacity: 0, scale: 0.9 },
        exitStyle: { x: 0, y: 10, opacity: 0, scale: 0.95 },
        height: "100%",
        width: "100%",
        backgroundColor: "$backgroundInversePureBlack",
        flexDirection: "column",
        $smallerThanMD: { justifyContent: "center" },
        px: 0,
        focusStyle: {
          borderColor: "$backgroundInversePureBlack"
        },
        children: [
          /* @__PURE__ */ jsxs(
            Dialog.Title,
            {
              flexDirection: "row",
              justifyContent: "space-between",
              onKeyDown: handleKeyDownEvents,
              $smallerThanMD: { flexDirection: "row-reverse" },
              width: "100%",
              "data-testid": "lightbox-content",
              mt: isLandscape && !isWeb ? "$20x" : null,
              $largerThanSM: { px: "$20x" },
              px: "$10x",
              display: "flex",
              alignItems: "center",
              mb: "$8x",
              children: [
                /* @__PURE__ */ jsxs(
                  Text,
                  {
                    tag: "span",
                    typography: "$bodyMedium",
                    color: "$contentInversePrimary",
                    "data-testid": "photo-count",
                    width: 64,
                    $largerThanSM: { textAlign: "left" },
                    textAlign: "right",
                    children: [
                      primaryPhotoIndex + 1,
                      " of ",
                      photoUrls.length
                    ]
                  }
                ),
                /* @__PURE__ */ jsx(Box, { flex: 1, justifyContent: "center", children: smallerThanMD && mobileTitle }),
                /* @__PURE__ */ jsx(Dialog.Close, { asChild: !0, children: /* @__PURE__ */ jsxs(
                  Box,
                  {
                    $smallerThanMD: {
                      width: "$64x"
                    },
                    children: [
                      /* @__PURE__ */ jsx(
                        CloseButton,
                        {
                          analyticsName: `desktop-${analyticsName}-close`,
                          onPress: handleClose,
                          size: "medium",
                          variant: "inverse",
                          $smallerThanMD: { display: "none" },
                          $largerThanSM: { display: "flex" }
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        ChevronBackButton,
                        {
                          analyticsName: `mobile-${analyticsName}-close`,
                          onPress: handleClose,
                          size: "medium",
                          variant: "inverse",
                          $smallerThanMD: { display: "flex" },
                          $largerThanSM: { display: "none" }
                        }
                      )
                    ]
                  }
                ) })
              ]
            }
          ),
          /* @__PURE__ */ jsx(
            View,
            {
              width: "100%",
              ...panResponder && panResponder.panHandlers,
              ref: carouselRef,
              "aria-label": `Image Carousel, showing image ${primaryPhotoIndex + 1} of ${photoUrls.length}`,
              flex: 1,
              children: /* @__PURE__ */ jsxs(
                Box,
                {
                  ref: containerRef,
                  height: "100%",
                  justifyContent: "center",
                  alignContent: "center",
                  aspectRatio: "3/2",
                  "data-testid": "lightbox-swipeable",
                  children: [
                    /* @__PURE__ */ jsx(
                      Animated.View,
                      {
                        style: {
                          flexDirection: "row",
                          width: windowWidth * photoUrls.length,
                          transform: [{ translateX: offsetX }],
                          marginBottom: 5,
                          flex: 1
                        },
                        children: renderImages()
                      }
                    ),
                    isWeb && /* @__PURE__ */ jsxs(Fragment, { children: [
                      /* @__PURE__ */ jsx(
                        ChevronBackButton,
                        {
                          "data-testid": "lightbox-back-button",
                          analyticsName: `${analyticsName}-lightbox-back-button`,
                          size: "medium",
                          $largerThanSM: { frameSize: "large", left: 32 },
                          $smallerThanMD: { display: "none" },
                          variant: "inverse",
                          onPress: handleChevronPress("left"),
                          position: "absolute",
                          left: 0,
                          ml: "$5x",
                          top: "40%",
                          display: "flex"
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        ChevronForwardButton,
                        {
                          "data-testid": "lightbox-forward-button",
                          analyticsName: `${analyticsName}-lightbox-forward-button`,
                          size: "medium",
                          onPress: handleChevronPress("right"),
                          $largerThanSM: { frameSize: "large", right: 32 },
                          $smallerThanMD: { display: "none" },
                          variant: "inverse",
                          position: "absolute",
                          right: 0,
                          mr: "$5x",
                          top: "40%",
                          display: "flex"
                        }
                      )
                    ] }),
                    largerThanSM && isWeb && /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(ScrollView, { horizontal: !0, showsHorizontalScrollIndicator: !1, children: photoUrls?.map((photo, index) => /* @__PURE__ */ jsx(
                      CarouselPhoto,
                      {
                        handleOnPress,
                        photoUrl: photo,
                        index,
                        primaryPhotoIndex,
                        isPrimary: index === primaryPhotoIndex,
                        photoRefs
                      },
                      index
                    )) }) }),
                    isWeb && largerThanSM && /* @__PURE__ */ jsx(Box, { flexDirection: "row", justifyContent: "space-between", p: "$8x", width: "100%", children: footer })
                  ]
                }
              )
            }
          )
        ]
      },
      "content"
    )
  ] }) });
};
Lightbox.displayName = "Lightbox";
const CarouselPhoto = ({
  handleOnPress,
  photoUrl,
  index,
  isPrimary,
  photoRefs
}) => {
  const onPress = useCallback(() => {
    handleOnPress(index);
  }, [handleOnPress, index]), IMG_CONTAINER_HEIGHT = 65, IMG_CONTAINER_WIDTH = 99, CONTAINER_HEIGHT = 73, photo = `${photoUrl}${CABINET_QUERY_PARAMS_LIGHTBOX_CAROUSEL}`, handleRef = (el) => {
    el && photoRefs.current && (photoRefs.current[index] = el);
  };
  return /* @__PURE__ */ jsx(
    PressableBox,
    {
      analyticsName: `lightbox-photo-item-${index}`,
      "aria-label": `Lightbox carousel photo ${index + 1}`,
      borderColor: isPrimary ? "$borderTertiary" : "$backgroundInversePureBlack",
      borderWidth: "$2x",
      height: CONTAINER_HEIGHT,
      $smallerThanMD: { display: "none" },
      onPress,
      "data-testid": `photo-item-${index}`,
      px: 0,
      ref: handleRef,
      children: /* @__PURE__ */ jsx(
        Image,
        {
          alt: `carousel photograph ${index + 1}`,
          source: { uri: photo },
          resizeMode: "cover",
          height: IMG_CONTAINER_HEIGHT,
          width: IMG_CONTAINER_WIDTH
        }
      )
    }
  );
};
export {
  Lightbox
};
